// The brought-in owner's pipeline, in one panel: where the application stands,
// the actions that apply to it right now (each a row in owner_pipeline_actions),
// the documents with Accept and Reject, the Onfleet inspection, and the
// timeline of everything that happened. Every rule lives in Postgres; this
// component collects what a person types and shows what comes back. A refusal
// from the database is shown as the database wrote it, because it was written
// for the operator.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, ExternalLink, KeyRound, ListChecks, Plus, RefreshCw, ScanSearch, Trash2, Upload as UploadIcon, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import {
  useApplyOwnerAction,
  useGetOwnerApplication,
  useGetOwnerDocumentLink,
  useReadOwnerDocument,
  useReviewOwnerDocument,
  useUploadOwnerDocument,
} from '../../hooks/backend/owners'
import { writeErrorMessage } from '../../utils/writeError'
import { stageLabel } from '../../utils/ownerBoard'
import type { OwnerRow } from '../../utils/owners'

type Action = {
  action_key: string
  label: string
  help_text: string | null
  to_status: string | null
  requires_note: boolean
  requires_reason: boolean
  is_destructive: boolean
  side_effect: string | null
}

type ChecklistItem = {
  doc_kind: string
  label: string
  required: boolean
  state: 'missing' | 'pending' | 'accepted' | 'rejected'
  reject_reason: string | null
  filename: string | null
  uploaded_at: string | null
}

export type Upload = {
  id: string
  doc_kind: string
  label: string
  stage: string | null
  filename: string | null
  size_bytes: number
  uploaded_by_kind: string
  uploaded_at: string | null
  review_state: string
  reviewed_at: string | null
  reject_reason: string | null
  reviewer: string | null
  // A password is saved for this PDF in Vault (0321). The password itself never
  // comes back to the browser.
  has_password?: boolean
  reading: Reading | null
}

// What the AI reader made of an upload (db/migrations/0291). Advisory only.
type Reading = {
  read_at: string
  status: 'read' | 'unreadable' | 'error'
  model: string | null
  detected_kind: string | null
  matches_expected: boolean | null
  overall: 'looks_right' | 'needs_a_look' | 'looks_wrong' | null
  summary: string | null
  extracted: Record<string, unknown>
  checks: { check: string; result: string; note?: string }[]
  authenticity_flags: { flag: string; severity: string; note?: string }[]
  error: string | null
}

type Defect = {
  description: string
  estimated_cost_zar: number | null
  remedy: 'before_commencement' | 'not_remedied'
  cost_bearer: 'owner' | 'fleetly' | null
}

type Inspection = {
  inspection_date: string | null
  outcome: 'pass' | 'fail' | null
  owner_stated_cost_zar: number | null
  cost_zar: number | null
  notes: string | null
  defects: Defect[]
} | null

type Detail = {
  application_id: string
  reference: string
  owner_model: string
  current_status: string
  car_label: string
  vehicle_year: number | null
  financed: boolean | null
  screener: Record<string, string | null> | null
  can_act: boolean
  actions: Action[]
  links: { live: number; latest_expiry: string | null; last_used_at: string | null } | null
  events: { at: string; kind: string; label: string; from_status: string | null; to_status: string | null; notes: string | null; actor: string | null }[]
  emails: { template_key: string; subject: string; to: string; status: string; queued_at: string; sent_at: string | null; failure_reason: string | null }[]
  terms_placeholders: string[] | null
  documents: {
    uploads: Upload[]
    checklists: Record<'documents' | 'onfleet' | 'contracting', ChecklistItem[]>
    inspection: Inspection
    workshop: { name?: string; address?: string; placeholder?: boolean } | null
  }
}

const MODELS: [string, string][] = [
  ['suzuki_dzire', 'Suzuki Dzire'],
  ['suzuki_swift', 'Suzuki Swift'],
  ['toyota_vitz', 'Toyota Vitz'],
  ['suzuki_celerio', 'Suzuki Celerio'],
  ['suzuki_spresso', 'Suzuki S-Presso'],
]

const REASONS: [string, string][] = [
  ['other', 'Other (say why in the note)'],
  ['meeting', 'Did not go forward after the meeting'],
  ['candidate_withdrew', 'The owner withdrew'],
  ['no_capacity', 'No capacity'],
  ['score', 'Does not meet the rules'],
]

const STATE_TONE: Record<string, string> = {
  missing: 'bg-muted text-muted-foreground',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  accepted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  superseded: 'bg-muted text-muted-foreground line-through',
}

const STATE_LABEL: Record<string, string> = {
  missing: 'Not uploaded',
  pending: 'Waiting for review',
  accepted: 'Accepted',
  rejected: 'Rejected',
  superseded: 'Replaced',
}

function when(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function rand(n: number | null | undefined): string {
  if (n == null) return '—'
  return `R${Number(n).toLocaleString('en-ZA', { maximumFractionDigits: 2 })}`
}

function blankDefect(): Defect {
  return { description: '', estimated_cost_zar: null, remedy: 'before_commencement', cost_bearer: 'owner' }
}

export function OwnerPipelineDialog({ owner, onChanged }: { owner: OwnerRow; onChanged: () => void }) {
  const [open, setOpen] = useState(false)
  const detailQ = useGetOwnerApplication()
  const trigger = detailQ.trigger
  const applicationId = owner.application_id

  const refresh = useCallback(() => {
    if (applicationId) trigger({ application_id: applicationId }, { skipCache: true })
  }, [trigger, applicationId])

  useEffect(() => {
    if (open) refresh()
  }, [open, refresh])

  const detail = detailQ.data as Detail | null

  const afterChange = useCallback(() => {
    refresh()
    onChanged()
  }, [refresh, onChanged])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <ListChecks className="h-3.5 w-3.5" />
          Pipeline
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{owner.full_name || 'Owner'}</DialogTitle>
          <DialogDescription>
            {detail ? `${detail.reference} · ${detail.car_label}${detail.vehicle_year ? ` ${detail.vehicle_year}` : ''}` : 'Manage My Car'}
          </DialogDescription>
        </DialogHeader>
        {detailQ.loading && !detail && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading
          </div>
        )}
        {detailQ.error && !detail && <p className="text-sm text-rose-600">{detailQ.error}</p>}
        {detail && <PipelineBody detail={detail} onChanged={afterChange} />}
      </DialogContent>
    </Dialog>
  )
}

function PipelineBody({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const placeholders = detail.terms_placeholders ?? []
  return (
    <div className="space-y-6 text-sm">
      <section className="rounded-lg border border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[hsl(var(--brand))] px-2.5 py-0.5 text-xs font-semibold text-white">
            {stageLabel(detail.current_status)}
          </span>
          <span className="text-xs text-muted-foreground">
            {detail.financed == null ? 'Finance not known yet' : detail.financed ? 'Financed' : 'Paid off'}
          </span>
          {detail.screener?.instalment && (
            <span className="text-xs text-muted-foreground">Instalment on the screener {rand(Number(detail.screener.instalment))}</span>
          )}
          {detail.screener?.mileage_km && (
            <span className="text-xs text-muted-foreground">
              {Number(detail.screener.mileage_km).toLocaleString('en-ZA')} km on the screener
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Onboarding links: {detail.links?.live ?? 0} live
          {detail.links?.latest_expiry ? `, the latest lasts until ${when(detail.links.latest_expiry)}` : ''}
          {detail.links?.last_used_at ? `, last opened ${when(detail.links.last_used_at)}` : ', not opened yet'}.
        </p>
        {detail.documents.workshop?.placeholder && detail.current_status === 'vehicle_assessment' && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            The Onfleet workshop shown to the owner is still the placeholder, 155 West Street.
          </p>
        )}
        {placeholders.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            Programme terms still undecided: {placeholders.join(', ').replace(/_/g, ' ')}.
          </p>
        )}
      </section>

      <Actions detail={detail} onChanged={onChanged} />
      <Documents detail={detail} onChanged={onChanged} />
      <InspectionSummary inspection={detail.documents.inspection} />
      <Timeline detail={detail} />
    </div>
  )
}

function Actions({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const [chosen, setChosen] = useState<Action | null>(null)
  if (!detail.can_act) {
    return (
      <section>
        <h3 className="mb-1 font-semibold">Next steps</h3>
        <p className="text-muted-foreground">Your login can see this application but not change it.</p>
      </section>
    )
  }
  return (
    <section>
      <h3 className="mb-2 font-semibold">Next steps</h3>
      {detail.actions.length === 0 ? (
        <p className="text-muted-foreground">No action applies at this stage.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {detail.actions.map((a) => (
            <Button
              key={a.action_key}
              size="sm"
              variant={chosen?.action_key === a.action_key ? 'default' : a.is_destructive ? 'outline' : 'secondary'}
              className={cn('h-8 text-xs', a.is_destructive && 'text-rose-700 dark:text-rose-300')}
              onClick={() => setChosen(chosen?.action_key === a.action_key ? null : a)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      )}
      {chosen && (
        <ActionForm
          key={chosen.action_key}
          action={chosen}
          applicationId={detail.application_id}
          onDone={() => {
            setChosen(null)
            onChanged()
          }}
        />
      )}
    </section>
  )
}

function ActionForm({ action, applicationId, onDone }: { action: Action; applicationId: string; onDone: () => void }) {
  const apply = useApplyOwnerAction()
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('other')
  const [busy, setBusy] = useState(false)
  // bi_car_registered
  const [modelKey, setModelKey] = useState('suzuki_swift')
  const [year, setYear] = useState('')
  const [km, setKm] = useState('')
  const [financed, setFinanced] = useState('yes')
  const [instalment, setInstalment] = useState('')
  // bi_extend_links
  const [days, setDays] = useState('30')
  // bi_send_onboarding_link
  const [purpose, setPurpose] = useState('after_meeting')
  // Onfleet
  const [inspectionDate, setInspectionDate] = useState('')
  const [cost, setCost] = useState('')
  const [defects, setDefects] = useState<Defect[]>([])

  const se = action.side_effect
  const isOnfleet = se === 'bi_onfleet_pass' || se === 'bi_onfleet_fail'

  function payload(): Record<string, unknown> {
    if (se === 'bi_recheck_and_send_link') {
      return {
        model_key: modelKey,
        first_registration_year: year,
        odometer_km: km,
        financed: financed === 'yes',
        instalment_zar: financed === 'yes' ? instalment : null,
      }
    }
    if (se === 'bi_extend_links') return { days }
    if (se === 'bi_send_onboarding_link') return { purpose }
    if (isOnfleet) return { inspection_date: inspectionDate, cost_zar: cost, defects }
    return {}
  }

  async function submit() {
    if (action.is_destructive && !window.confirm(`${action.label}? ${action.help_text ?? ''}`)) return
    setBusy(true)
    try {
      await apply.trigger({
        application_id: applicationId,
        action_key: action.action_key,
        notes: notes.trim() || null,
        reason: action.requires_reason ? reason : null,
        payload: payload(),
      }).result
      toast.success(action.label, { description: action.to_status ? `Now at ${stageLabel(action.to_status)}.` : 'Recorded.' })
      onDone()
    } catch (err) {
      toast.error('Not done', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      {action.help_text && <p className="text-xs text-muted-foreground">{action.help_text}</p>}

      {se === 'bi_recheck_and_send_link' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label>Model, from the RC1</Label>
            <NativeSelect value={modelKey} onChange={(e) => setModelKey(e.target.value)}>
              {MODELS.map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1">
            <Label>Year of first registration</Label>
            <Input inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" />
          </div>
          <div className="space-y-1">
            <Label>Odometer, km</Label>
            <Input inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Financed</Label>
            <NativeSelect value={financed} onChange={(e) => setFinanced(e.target.value)}>
              <option value="yes">Yes</option>
              <option value="no">No, paid in cash</option>
            </NativeSelect>
          </div>
          {financed === 'yes' && (
            <div className="space-y-1">
              <Label>Monthly instalment, R</Label>
              <Input inputMode="decimal" value={instalment} onChange={(e) => setInstalment(e.target.value)} />
            </div>
          )}
        </div>
      )}

      {se === 'bi_extend_links' && (
        <div className="max-w-[12rem] space-y-1">
          <Label>Extend by, days</Label>
          <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} />
        </div>
      )}

      {se === 'bi_send_onboarding_link' && (
        <div className="space-y-1">
          <Label>The email opens with</Label>
          <NativeSelect value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            <option value="after_meeting">Thank you for meeting with us</option>
            <option value="staff_send">Here is a new link to your onboarding page</option>
          </NativeSelect>
        </div>
      )}

      {isOnfleet && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Inspection date on the report</Label>
              <Input type="date" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Cost Fleetly reimburses, R</Label>
              <Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Blank: what the owner entered" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Defects on the report</Label>
              <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => setDefects([...defects, blankDefect()])}>
                <Plus className="h-3.5 w-3.5" /> Add a defect
              </Button>
            </div>
            {defects.length === 0 && <p className="text-xs text-muted-foreground">No defects entered.</p>}
            {defects.map((d, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 rounded-md border border-border bg-background p-2">
                <Input className="col-span-12 sm:col-span-5" placeholder="What the report says" value={d.description}
                  onChange={(e) => setDefects(defects.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
                <Input className="col-span-4 sm:col-span-2" inputMode="decimal" placeholder="R cost" value={d.estimated_cost_zar ?? ''}
                  onChange={(e) => setDefects(defects.map((x, j) => (j === i ? { ...x, estimated_cost_zar: e.target.value === '' ? null : Number(e.target.value) } : x)))} />
                <NativeSelect className="col-span-4 sm:col-span-2" value={d.remedy}
                  onChange={(e) => setDefects(defects.map((x, j) => (j === i ? { ...x, remedy: e.target.value as Defect['remedy'] } : x)))}>
                  <option value="before_commencement">Fixed first</option>
                  <option value="not_remedied">Not fixed</option>
                </NativeSelect>
                <NativeSelect className="col-span-3 sm:col-span-2" value={d.remedy === 'not_remedied' ? 'owner' : d.cost_bearer ?? 'owner'}
                  disabled={d.remedy === 'not_remedied'}
                  onChange={(e) => setDefects(defects.map((x, j) => (j === i ? { ...x, cost_bearer: e.target.value as 'owner' | 'fleetly' } : x)))}>
                  <option value="owner">Owner pays</option>
                  <option value="fleetly">Fleetly pays</option>
                </NativeSelect>
                <Button type="button" size="sm" variant="ghost" className="col-span-1 h-9 px-0" aria-label="Remove defect"
                  onClick={() => setDefects(defects.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              A defect not fixed before the car starts is the owner's cost and does not count against the R15,000 cap (clause 2.7).
            </p>
          </div>
        </div>
      )}

      {action.requires_reason && (
        <div className="space-y-1">
          <Label>Reason</Label>
          <NativeSelect value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </NativeSelect>
        </div>
      )}

      <div className="space-y-1">
        <Label>Note{action.requires_note ? '' : ' (optional)'}</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder={se === 'bi_onfleet_fail' ? 'Shown to the owner in the email' : undefined} />
      </div>
      <Button size="sm" onClick={submit} disabled={busy || (action.requires_note && notes.trim() === '')}>
        {busy ? <Spinner /> : null}
        {action.label}
      </Button>
    </div>
  )
}

function Documents({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const uploads = detail.documents.uploads
  const live = useMemo(() => uploads.filter((u) => u.review_state !== 'superseded'), [uploads])
  const replaced = useMemo(() => uploads.filter((u) => u.review_state === 'superseded'), [uploads])
  const lists = detail.documents.checklists
  return (
    <section>
      <h3 className="mb-2 font-semibold">Documents</h3>
      {(['documents', 'onfleet', 'contracting'] as const).map((stage) => {
        const items = lists[stage] ?? []
        if (items.length === 0) return null
        const done = items.filter((x) => x.required && x.state === 'accepted').length
        const need = items.filter((x) => x.required).length
        return (
          <div key={stage} className="mb-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {stage === 'documents' ? 'Step one: documents' : stage === 'onfleet' ? 'Step two: Onfleet' : 'Step three: contracting'} · {done} of {need} accepted
            </p>
            <ul className="divide-y divide-border rounded-md border border-border">
              {items.map((it) => {
                const up = live.find((u) => u.doc_kind === it.doc_kind)
                return (
                  <li key={it.doc_kind} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <span className="min-w-0 flex-1">
                      {it.label}
                      {!it.required && <span className="text-muted-foreground"> (if it applies)</span>}
                    </span>
                    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', STATE_TONE[it.state])}>
                      {STATE_LABEL[it.state]}
                    </span>
                    {up && <DocumentButtons upload={up} canAct={detail.can_act} onChanged={onChanged} />}
                    {detail.can_act && it.state !== 'accepted' && (
                      <OwnerDocUpload applicationId={detail.application_id} docKind={it.doc_kind} onDone={onChanged} />
                    )}
                    {it.state === 'rejected' && it.reject_reason && (
                      <p className="w-full text-xs text-rose-700 dark:text-rose-300">Rejected: {it.reject_reason}</p>
                    )}
                    {up && <ReadingView upload={up} onChanged={onChanged} />}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
      {replaced.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">{replaced.length} earlier upload{replaced.length === 1 ? '' : 's'}, replaced</summary>
          <ul className="mt-1 space-y-1">
            {replaced.map((u) => (
              <li key={u.id} className="flex items-center gap-2">
                <span className="flex-1">{u.label}: {u.filename ?? 'file'} ({when(u.uploaded_at)}){u.reject_reason ? `, rejected: ${u.reject_reason}` : ''}</span>
                <ViewButton id={u.id} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

const OVERALL: Record<string, { label: string; tone: string }> = {
  looks_right: { label: 'Reader: looks right', tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  needs_a_look: { label: 'Reader: needs a look', tone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  looks_wrong: { label: 'Reader: looks wrong', tone: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
}

// The reader is a second pair of eyes, not a decision: nothing it says accepts
// or rejects a document. So it sits under the row, collapsed to one line, and
// the Accept and Reject buttons above it do not depend on it.
export function ReadingView({ upload, onChanged }: { upload: Upload; onChanged: () => void }) {
  const readAgain = useReadOwnerDocument()
  const [busy, setBusy] = useState(false)
  const r = upload.reading
  async function again() {
    setBusy(true)
    try {
      await readAgain.trigger({ document_id: upload.id }).result
      toast.success('Reading again', { description: 'The result appears here in about a minute. Refresh the panel to see it.' })
      setTimeout(onChanged, 45000)
    } catch (err) {
      toast.error('Could not start the reader', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  const againButton = (
    <>
      <Button size="sm" variant="ghost" className="h-6 gap-1 px-1.5 text-[11px]" onClick={again} disabled={busy}>
        <RefreshCw className="h-3 w-3" /> Read again
      </Button>
      <PasswordButton upload={upload} onChanged={onChanged} />
    </>
  )
  if (!r) {
    return (
      <div className="flex w-full items-center gap-2 text-[11px] text-muted-foreground">
        <ScanSearch className="h-3.5 w-3.5" /> Reader: not read yet{upload.uploaded_at ? ', usually ready a minute after upload' : ''}. {againButton}
      </div>
    )
  }
  if (r.status !== 'read') {
    return (
      <div className="flex w-full flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <ScanSearch className="h-3.5 w-3.5" /> Reader could not read it: {r.error ?? r.status}. {againButton}
      </div>
    )
  }
  const o = OVERALL[r.overall ?? 'needs_a_look'] ?? OVERALL.needs_a_look
  const failed = r.checks.filter((c) => c.result === 'fail').length
  const unclear = r.checks.filter((c) => c.result === 'unclear').length
  const flags = r.authenticity_flags.filter((f) => f.severity !== 'low')
  return (
    <details className="w-full rounded-md bg-muted/40 px-2 py-1.5 text-xs">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2">
        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', o.tone)}>{o.label}</span>
        {failed > 0 && <span className="text-rose-700 dark:text-rose-300">{failed} check{failed === 1 ? '' : 's'} failed</span>}
        {unclear > 0 && <span className="text-muted-foreground">{unclear} unclear</span>}
        {flags.length > 0 && <span className="font-medium text-rose-700 dark:text-rose-300">{flags.length} possible tampering sign{flags.length === 1 ? '' : 's'}</span>}
        <span className="text-muted-foreground">{when(r.read_at)}</span>
      </summary>
      <div className="mt-2 space-y-2">
        {r.summary && <p>{r.summary}</p>}
        {r.matches_expected === false && (
          <p className="text-rose-700 dark:text-rose-300">This does not look like the document asked for. The reader saw: {r.detected_kind}.</p>
        )}
        <ul className="space-y-1">
          {r.checks.map((c, i) => (
            <li key={i}>
              <span className={cn('font-medium', c.result === 'pass' ? 'text-emerald-700 dark:text-emerald-300' : c.result === 'fail' ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300')}>
                {c.result === 'pass' ? 'Pass' : c.result === 'fail' ? 'Fail' : 'Unclear'}
              </span>{' '}
              {c.check}
              {c.note ? <span className="text-muted-foreground">: {c.note}</span> : null}
            </li>
          ))}
        </ul>
        {r.authenticity_flags.length > 0 && (
          <div>
            <p className="font-medium">Signs of tampering the reader noticed</p>
            <ul className="list-disc pl-4">
              {r.authenticity_flags.map((f, i) => (
                <li key={i}>
                  <span className="font-medium">{f.severity}</span>: {f.flag}
                  {f.note ? <span className="text-muted-foreground">, {f.note}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {Object.keys(r.extracted ?? {}).length > 0 && (
          <p className="text-muted-foreground">
            Read from the document:{' '}
            {Object.entries(r.extracted).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join('; ')}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">
          The reader is a second pair of eyes. It can miss a careful forgery and can flag an honest photo. You decide. {againButton}
        </p>
      </div>
    </details>
  )
}

// A password for a locked PDF (0321). Saving it reads the document again with
// it; a blank one clears it, because blank means "this has no password".
function PasswordButton({ upload, onChanged }: { upload: Upload; onChanged: () => void }) {
  const read = useReadOwnerDocument()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  async function save() {
    setBusy(true)
    try {
      await read.trigger({ document_id: upload.id, set_password: true, password: value.trim() }).result
      toast.success(value.trim() ? 'Password saved, reading again' : 'Password cleared, reading again', {
        description: 'The result appears here in about a minute.',
      })
      setOpen(false)
      setValue('')
      setTimeout(onChanged, 45000)
    } catch (err) {
      toast.error('Not saved', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  if (!open) {
    return (
      <Button size="sm" variant="ghost" className="h-6 gap-1 px-1.5 text-[11px]" onClick={() => setOpen(true)}>
        <KeyRound className="h-3 w-3" /> {upload.has_password ? 'Password saved, change' : 'Password'}
      </Button>
    )
  }
  return (
    <span className="flex w-full flex-wrap items-center gap-2">
      <Input
        className="h-7 w-56 text-xs"
        autoFocus
        autoComplete="off"
        spellCheck={false}
        maxLength={100}
        placeholder={upload.has_password ? 'New password, or blank to clear' : "The document's password"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save()
        }}
      />
      <Button size="sm" className="h-7 px-2 text-xs" onClick={save} disabled={busy || (!value.trim() && !upload.has_password)}>
        {value.trim() ? 'Save and read' : 'Clear and read'}
      </Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </span>
  )
}

// A document the owner sent some other way (email, WhatsApp, by hand), filed
// for them (0334). It is read and reviewed exactly like the owner's own upload.
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? (reader.result.split(',')[1] ?? '') : '')
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

export function OwnerDocUpload({ applicationId, docKind, onDone, label }: { applicationId: string; docKind: string; onDone: () => void; label?: string }) {
  const upload = useUploadOwnerDocument()
  const [busy, setBusy] = useState(false)
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      toast.error('The file must be smaller than 10 MB.')
      return
    }
    if (!['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Upload a PDF, a JPG or a PNG.')
      return
    }
    setBusy(true)
    try {
      await upload.trigger({
        application_id: applicationId,
        doc_kind: docKind,
        filename: file.name,
        contentType: file.type,
        contentBase64: await readAsBase64(file),
      }).result
      toast.success('Filed for the owner', { description: 'The reader reads it in about a minute.' })
      onDone()
      setTimeout(onDone, 45000)
    } catch (err) {
      toast.error('Not filed', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <label
      className={cn(
        'inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border border-input px-2 text-xs hover:bg-muted',
        busy && 'pointer-events-none opacity-50',
      )}
      title="File a document the owner sent some other way"
    >
      {busy ? <Spinner className="h-3.5 w-3.5" /> : <UploadIcon className="h-3.5 w-3.5" />} {label ?? 'Upload for the owner'}
      <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} disabled={busy} />
    </label>
  )
}

export function ViewButton({ id }: { id: string }) {
  const link = useGetOwnerDocumentLink()
  const [busy, setBusy] = useState(false)
  async function view() {
    setBusy(true)
    // Open the tab inside the click, before the await, or a browser blocks it
    // as a popup.
    const tab = window.open('about:blank', '_blank')
    try {
      const r = (await link.trigger({ document_id: id }, { skipCache: true })) as { url: string } | null
      if (r?.url && tab) tab.location.href = r.url
      else tab?.close()
    } catch (err) {
      tab?.close()
      toast.error('Could not open the document', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={view} disabled={busy}>
      <ExternalLink className="h-3.5 w-3.5" /> View
    </Button>
  )
}

export function DocumentButtons({ upload, canAct, onChanged }: { upload: Upload; canAct: boolean; onChanged: () => void }) {
  const review = useReviewOwnerDocument()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  async function decide(decision: 'accept' | 'reject') {
    setBusy(true)
    try {
      const r = (await review.trigger({ document_id: upload.id, decision, reason: decision === 'reject' ? reason : null }).result) as
        | { stage_advanced?: boolean; to_status?: string }
        | null
      toast.success(decision === 'accept' ? 'Accepted' : 'Rejected, the owner has been emailed', {
        description: r?.stage_advanced && r.to_status ? `Every required document is accepted. Now at ${stageLabel(r.to_status)}.` : undefined,
      })
      setRejecting(false)
      setReason('')
      onChanged()
    } catch (err) {
      toast.error('Not done', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ViewButton id={upload.id} />
      {canAct && upload.review_state === 'pending' && !rejecting && (
        <>
          <Button size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => decide('accept')} disabled={busy}>
            <Check className="h-3.5 w-3.5" /> Accept
          </Button>
          <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => setRejecting(true)} disabled={busy}>
            <X className="h-3.5 w-3.5" /> Reject
          </Button>
        </>
      )}
      {rejecting && (
        <div className="flex w-full flex-wrap items-center gap-2">
          <Input className="h-8 flex-1" autoFocus placeholder="Why, written for the owner. It goes in the email." value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" className="h-8 text-xs" onClick={() => decide('reject')} disabled={busy || reason.trim().length < 5}>
            Reject and email
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setRejecting(false)}>Cancel</Button>
        </div>
      )}
    </>
  )
}

function InspectionSummary({ inspection }: { inspection: Inspection }) {
  if (!inspection) return null
  return (
    <section>
      <h3 className="mb-1 font-semibold">Onfleet inspection</h3>
      <p className="text-muted-foreground">
        {inspection.outcome ? (inspection.outcome === 'pass' ? 'Passed' : 'Failed') : 'Report uploaded, outcome not recorded'}
        {inspection.inspection_date ? ` on ${inspection.inspection_date}` : ''}. Owner says they paid {rand(inspection.owner_stated_cost_zar)}
        {inspection.cost_zar != null ? `; reimbursed with the second month: ${rand(inspection.cost_zar)}` : ''}.
      </p>
      {inspection.defects.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-muted-foreground">
          {inspection.defects.map((d, i) => (
            <li key={i}>
              {d.description} ({rand(d.estimated_cost_zar)}):{' '}
              {d.remedy === 'not_remedied' ? 'not fixed, owner carries it' : `fixed first, ${d.cost_bearer === 'fleetly' ? 'Fleetly' : 'owner'} pays`}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Timeline({ detail }: { detail: Detail }) {
  return (
    <section className="grid gap-4 sm:grid-cols-2">
      <div>
        <h3 className="mb-1 font-semibold">What happened</h3>
        <ul className="space-y-1.5">
          {detail.events.slice(0, 25).map((e, i) => (
            <li key={i} className="text-xs">
              <span className="text-muted-foreground">{when(e.at)}</span>{' '}
              {e.label}
              {e.actor ? <span className="text-muted-foreground">, {e.actor}</span> : null}
              {e.notes ? <span className="block text-muted-foreground">{e.notes}</span> : null}
            </li>
          ))}
          {detail.events.length === 0 && <li className="text-xs text-muted-foreground">Nothing recorded yet.</li>}
        </ul>
      </div>
      <div>
        <h3 className="mb-1 font-semibold">Emails</h3>
        <ul className="space-y-1.5">
          {detail.emails.slice(0, 25).map((m, i) => (
            <li key={i} className="text-xs">
              <span className="text-muted-foreground">{when(m.queued_at)}</span> {m.subject}
              <span className="block text-muted-foreground">
                to {m.to}, {m.status}
                {m.failure_reason ? `: ${m.failure_reason}` : ''}
              </span>
            </li>
          ))}
          {detail.emails.length === 0 && <li className="text-xs text-muted-foreground">No emails yet.</li>}
        </ul>
      </div>
    </section>
  )
}
